import { useQuery } from '@tanstack/react-query';
import { RegisterScreen } from '@/components/RegisterScreen';
import { ChildRecords } from '@/components/ChildRecords';
import { StatusBadge } from '@/components/live';
import { listRows } from '@/api/resources';
import { usePickers } from '@/hooks/usePickers';
import { isSupabaseConfigured } from '@/lib/supabase';

interface TemplateRow {
  id: string; workflow_code: string; name: string; module_key: string; record_type: string;
  is_active: boolean; self_approval_blocked: boolean; step_count: number; open_requests: number;
}

interface StepRow {
  id: string; step_no: number; step_name: string; approver_role_name: string | null; reviewer_role_name: string | null;
  specific_user_name: string | null; route_next_level: boolean; sla_hours: number | null; requires_mfa: boolean;
}

/** The record types the application submits for approval, in words. */
const RECORD_TYPES: Record<string, string> = {
  farm_plot: 'Farm plots', party_kyc_record: 'KYC records', plot_harvest_entry: 'Harvest entries',
  plot_species_inventory: 'Tree inventory', plot_tenure_arrangement: 'Tenure overlaps', any: 'Any change to a locked record',
};

function decider(row: StepRow): string {
  if (row.specific_user_name) return row.specific_user_name;
  if (row.route_next_level) return 'The next level up from whoever submitted';
  return [row.approver_role_name, row.reviewer_role_name].filter((v, i, all) => v && all.indexOf(v) === i).join(' or ') || 'Nobody named';
}

function Steps({ template }: { template: TemplateRow }) {
  const pick = usePickers(['users']);
  const roles = useQuery({
    queryKey: ['roles-picker'], enabled: isSupabaseConfigured,
    queryFn: () => listRows<{ id: string; name: string; tier: number; status: string }>('roles', { orderBy: { column: 'tier' } }),
  });
  const roleOptions = (roles.data ?? []).filter((row) => row.status === 'active').map((row) => ({ id: row.id, label: `${row.name} (tier ${row.tier})` }));
  return (
    <ChildRecords<StepRow>
      addLabel="Add step" emptyText="No steps. A workflow with no steps cannot be switched on."
      filters={{ workflow_template_id: template.id }} ownerId={template.id} parent={{ workflow_template_id: template.id }}
      screenKey="workflow.configuration" table="wf.workflow_steps" title="Steps" view="workflow_steps"
      orderBy={{ column: 'step_no', ascending: true }}
      intro={<p className="muted">Requests go through the steps in order. Each step says who may decide it.</p>}
      defaults={{ step_no: String((template.step_count ?? 0) + 1), sla_hours: '48', route_next_level: false, requires_mfa: false }}
      fields={[
        { key: 'step_no', label: 'Order', type: 'number', required: true },
        { key: 'step_name', label: 'Step', type: 'text', required: true, placeholder: 'e.g. Compliance review' },
        { key: 'route_next_level', label: 'Next level up', type: 'check', placeholder: 'Whoever sits above the person who submitted decides' },
        { key: 'approver_role_id', label: 'Approver role', type: 'select', options: roleOptions, placeholder: 'No role',
          showIf: (form) => !form.route_next_level },
        { key: 'reviewer_role_id', label: 'Or reviewer role', type: 'select', options: roleOptions, placeholder: 'No role',
          showIf: (form) => !form.route_next_level },
        { key: 'specific_user_id', label: 'Or one named user', type: 'select', options: pick.users, placeholder: 'Anyone in the role',
          showIf: (form) => !form.route_next_level, hint: 'Only this person may decide. Leave empty to let the role decide.' },
        { key: 'sla_hours', label: 'Time allowed (hours)', type: 'number', hint: 'Shown as the due time on the request. 1 to 2160.' },
        { key: 'requires_mfa', label: 'Second factor', type: 'check', placeholder: 'The decider confirms with their approval PIN' },
      ]}
      validate={(form) => ({
        approver_role_id: !form.route_next_level && !form.approver_role_id && !form.reviewer_role_id && !form.specific_user_id
          ? 'Say who decides: a role, a named user, or the next level up.' : undefined,
      })}
      summary={(row) => ({
        title: `${row.step_no}. ${row.step_name}`,
        badge: row.requires_mfa ? <StatusBadge label="PIN" tone="blue" /> : undefined,
        detail: [decider(row), row.sla_hours ? `${row.sla_hours} h allowed` : null].filter(Boolean).join(' · '),
      })}
    />
  );
}

/**
 * Approval workflows (`workflows`, back from the prototypes on 2026-09-27). The engine is real:
 * submitting a plot, a KYC record, a harvest or tree inventory picks the active workflow for its
 * record type, and each step says who may decide it. Workflows are matched to record types in code,
 * so they are edited here, never created. Changing them needs the platform owner's tier; an
 * organisation admin can read them.
 */
export default function WorkflowsScreen() {
  return (
    <RegisterScreen<TemplateRow>
      canAdd={false}
      addLabel="Workflow" exportName="approval-workflows" recordLabel={(row) => `workflow ${row.name}`} recordNoun="workflow"
      screenKey="workflow.configuration" searchColumns={['name', 'workflow_code', 'record_type']}
      subtitle="Who approves what: the steps each kind of record goes through, and who may decide each step."
      table="wf.workflow_templates" title="Approval workflows" view="workflow_templates" orderBy={{ column: 'name', ascending: true }}
      columns={[
        { key: 'name', label: 'Workflow', render: (row) => <span className="cell-stack"><strong>{row.name}</strong><small className="muted">{row.workflow_code}</small></span> },
        { key: 'record_type', label: 'Applies to', render: (row) => RECORD_TYPES[row.record_type] ?? row.record_type.replace(/_/g, ' ') },
        { key: 'step_count', label: 'Steps', align: 'right' },
        { key: 'open_requests', label: 'Open requests', align: 'right' },
        { key: 'self_approval_blocked', label: 'Self-approval', render: (row) => (row.self_approval_blocked ? 'Blocked' : 'Allowed') },
        { key: 'is_active', label: 'Status', render: (row) => <StatusBadge label={row.is_active ? 'On' : 'Off'} tone={row.is_active ? 'green' : 'gray'} /> },
      ]}
      stats={[
        { key: 'on', label: 'On', tone: 'ok', match: (row) => row.is_active },
        { key: 'off', label: 'Off', tone: 'neutral', match: (row) => !row.is_active },
        { key: 'open', label: 'With open requests', tone: 'warn', match: (row) => row.open_requests > 0 },
        { key: 'self', label: 'Self-approval allowed', tone: 'error', match: (row) => !row.self_approval_blocked },
      ]}
      fields={[
        { key: 'name', label: 'Name', type: 'text', required: true },
        { key: 'is_active', label: 'On', type: 'check', placeholder: 'Records of this kind go through this workflow when submitted' },
        { key: 'self_approval_blocked', label: 'Self-approval', type: 'check', placeholder: 'Nobody may approve a record they created or changed' },
      ]}
    >
      {(row) => <Steps template={row} />}
    </RegisterScreen>
  );
}
